'use client';

import { ShowMoreDetailsProps } from '@/@studio-ai/props';
import { TokenUsage } from '@/@studio-ai/types';
import { Divider } from '@heroui/react';
import { Icon } from '@iconify/react';
import { AnimatePresence, motion } from 'framer-motion';
import { useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { STREAMING_EVENT_TYPES, UI_VARIANTS } from '../constants';
import { CodeDisplay } from './code-display';
import { markdownComponents } from './markdown';

export function ShowMoreDetails({ content, metadata, className = '' }: ShowMoreDetailsProps) {
    const [isExpanded, setIsExpanded] = useState(false);

    const tokenUsage = useMemo(() => {
        if (!metadata?.usage) return null;

        const rawUsage = metadata.usage as TokenUsage;

        if (
            rawUsage.tokens_consumed &&
            Array.isArray(rawUsage.tokens_consumed) &&
            rawUsage.tokens_consumed.length > 0
        ) {
            const tokenData = rawUsage.tokens_consumed[0];
            const inputTokens = tokenData.input_tokens?.uncached ?? 0;
            const outputTokens = tokenData.output_tokens?.total ?? 0;

            return {
                input_tokens: inputTokens,
                output_tokens: outputTokens,
                total_tokens: inputTokens + outputTokens,
                model: tokenData.model_name,
            } as TokenUsage;
        }

        return rawUsage;
    }, [metadata]);

    const processedContent: Array<{
        type:
            | typeof UI_VARIANTS.DETAIL_TYPE.THINKING
            | typeof UI_VARIANTS.DETAIL_TYPE.SQL
            | typeof UI_VARIANTS.DETAIL_TYPE.TOOL_USE;
        content: string;
        index: number;
        toolMeta?: {
            name: string;
            toolType?: string;
            query?: string;
        };
    }> = [];

    content.forEach((item, index) => {
        if (item.type === STREAMING_EVENT_TYPES.THINKING && (item.text || item.thinking)) {
            processedContent.push({
                type: UI_VARIANTS.DETAIL_TYPE.THINKING,
                content: item.text || item.thinking?.text || '',
                index,
            });
        } else if (item.type === STREAMING_EVENT_TYPES.TOOL_RESULT && item.tool_result?.content) {
            const toolResultsContent = item.tool_result.content;
            if (Array.isArray(toolResultsContent)) {
                toolResultsContent.forEach((result) => {
                    if (result.json && typeof result.json === 'object' && 'sql' in result.json) {
                        const sql = result.json.sql as string;
                        if (sql && sql.trim()) {
                            processedContent.push({
                                type: UI_VARIANTS.DETAIL_TYPE.SQL,
                                content: sql,
                                index,
                            });
                        }
                    }
                });
            }
        } else if (item.type === STREAMING_EVENT_TYPES.TOOL_USE && item.tool_use?.name) {
            const toolUse = item.tool_use as {
                name: string;
                type?: string;
                input?: { query?: string; original_query?: string };
            };
            processedContent.push({
                type: UI_VARIANTS.DETAIL_TYPE.TOOL_USE,
                content: toolUse.name,
                index,
                toolMeta: {
                    name: toolUse.name,
                    toolType: toolUse.type,
                    query: toolUse.input?.query || toolUse.input?.original_query,
                },
            });
        }
    });

    if (processedContent.length === 0 && !tokenUsage) {
        return null;
    }

    const formatNumber = (num: number | undefined) => {
        if (num === undefined || num === null) return '-';
        return num.toLocaleString();
    };

    const panelId = 'show-more-details-panel';

    return (
        <div className={`w-full ${className}`}>
            <button
                type="button"
                className="flex cursor-pointer items-center gap-1 bg-transparent p-0 text-mini text-default-400 transition-colors hover:text-default-600"
                onClick={() => setIsExpanded(!isExpanded)}
                aria-expanded={isExpanded}
                aria-controls={panelId}
            >
                <span className="text-medium">Details</span>
                <Icon
                    icon={isExpanded ? 'lucide:chevron-up' : 'lucide:chevron-down'}
                    width={14}
                    height={14}
                />
            </button>

            <AnimatePresence>
                {isExpanded && (
                    <motion.div
                        id={panelId}
                        role="region"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                    >
                        <div className="mt-2 max-h-80 overflow-auto rounded-lg border border-default-500 p-4">
                            <div className="space-y-4">
                                {tokenUsage && (
                                    <div className="space-y-1.5">
                                        <div className="flex items-center gap-2 text-small font-medium text-default-600">
                                            <Icon icon="lucide:coins" width={14} height={14} />
                                            <span>Token Usage</span>
                                        </div>
                                        <ul className="list-inside list-disc space-y-0.5 pl-1 text-mini text-default-600">
                                            <li>
                                                Input:{' '}
                                                <span className="font-mono">
                                                    {formatNumber(tokenUsage.input_tokens)}
                                                </span>
                                            </li>
                                            <li>
                                                Output:{' '}
                                                <span className="font-mono">
                                                    {formatNumber(tokenUsage.output_tokens)}
                                                </span>
                                            </li>
                                            <li>
                                                Total:{' '}
                                                <span className="font-mono font-medium text-primary">
                                                    {formatNumber(tokenUsage.total_tokens)}
                                                </span>
                                            </li>
                                            {typeof tokenUsage.model === 'string' && (
                                                <li>
                                                    Model:{' '}
                                                    <span className="font-mono">
                                                        {tokenUsage.model}
                                                    </span>
                                                </li>
                                            )}
                                        </ul>
                                    </div>
                                )}

                                {tokenUsage && processedContent.length > 0 && (
                                    <Divider className="my-2" />
                                )}

                                {processedContent.map((item, index) => (
                                    <div key={`${item.type}-${index}`}>
                                        {item.type === UI_VARIANTS.DETAIL_TYPE.THINKING ? (
                                            <div className="prose prose-sm max-w-none text-mini leading-relaxed text-default-600">
                                                <ReactMarkdown
                                                    remarkPlugins={[remarkGfm]}
                                                    components={markdownComponents}
                                                >
                                                    {item.content}
                                                </ReactMarkdown>
                                            </div>
                                        ) : item.type === UI_VARIANTS.DETAIL_TYPE.TOOL_USE &&
                                          item.toolMeta ? (
                                            <div className="rounded-md bg-default-100 p-3">
                                                <div className="flex items-center gap-2 text-small font-medium text-default-600">
                                                    <Icon
                                                        icon="lucide:wrench"
                                                        width={14}
                                                        height={14}
                                                        className="text-primary"
                                                    />
                                                    <span>{item.toolMeta.name}</span>
                                                </div>
                                                {item.toolMeta.query && (
                                                    <div className="mt-2 text-mini text-default-500">
                                                        {item.toolMeta.query}
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <div className="mt-3">
                                                <CodeDisplay
                                                    code={item.content}
                                                    language="sql"
                                                    title=""
                                                    collapsible={false}
                                                />
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
